import { type KeyboardEvent } from "react";
import { assert } from "utils";

// The sets table looks like a spreadsheet: gridlines between cells, compact
// rows and inputs that fill their cell.
export const SET_HEAD_CELL =
  "h-8 border-r px-2 py-1 text-xs whitespace-nowrap last:border-r-0";

export const SET_BODY_CELL = "h-9 border-r p-0 last:border-r-0";

// The focused cell gets an inset outline, like Excel's selected cell.
export const SET_INPUT_CELL = [
  "focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-ring",
  "[&_input]:h-9 [&_input]:w-full [&_input]:min-w-16 [&_input]:rounded-none [&_input]:border-0 [&_input]:bg-transparent [&_input]:px-0 [&_input]:text-left [&_input]:shadow-none",
  "[&_input:focus-visible]:ring-0",
].join(" ");

function isWholeValueSelected(input: HTMLInputElement): boolean {
  return (
    input.selectionStart === 0 && input.selectionEnd === input.value.length
  );
}

function canLeaveInput(input: HTMLInputElement, key: string): boolean {
  if (key !== "ArrowLeft" && key !== "ArrowRight") {
    return true;
  }

  if (isWholeValueSelected(input)) {
    return true;
  }

  if (input.selectionStart !== input.selectionEnd) {
    return false;
  }

  return key === "ArrowLeft"
    ? input.selectionStart === 0
    : input.selectionEnd === input.value.length;
}

function findCellInput(cell: Element | null | undefined) {
  return cell?.querySelector<HTMLInputElement>("input:not(:disabled)") ?? null;
}

function findInputInRow(
  row: Element | null,
  cellIndex: number,
): HTMLInputElement | null {
  return findCellInput(row?.children[cellIndex]);
}

function findInputInSameRow(
  cell: HTMLTableCellElement,
  sibling: "previousElementSibling" | "nextElementSibling",
): HTMLInputElement | null {
  for (let next = cell[sibling]; next; next = next[sibling]) {
    const input = findCellInput(next);

    if (input) {
      return input;
    }
  }

  return null;
}

function findTargetInput(
  cell: HTMLTableCellElement,
  event: KeyboardEvent<HTMLElement>,
): HTMLInputElement | null {
  const row = cell.parentElement;
  assert(row, "Set cell has no row");

  switch (event.key) {
    case "ArrowUp":
      return findInputInRow(row.previousElementSibling, cell.cellIndex);
    case "ArrowDown":
      return findInputInRow(row.nextElementSibling, cell.cellIndex);
    case "Enter":
      return findInputInRow(
        event.shiftKey ? row.previousElementSibling : row.nextElementSibling,
        cell.cellIndex,
      );
    case "ArrowLeft":
      return findInputInSameRow(cell, "previousElementSibling");
    case "ArrowRight":
      return findInputInSameRow(cell, "nextElementSibling");
    default:
      return null;
  }
}

// Modified arrows edit text (Shift extends the selection, Alt jumps words),
// and Enter during IME composition commits the composed text.
function isTextEditingKey(event: KeyboardEvent<HTMLElement>): boolean {
  return (
    event.nativeEvent.isComposing ||
    event.altKey ||
    event.metaKey ||
    event.ctrlKey ||
    (event.shiftKey && event.key !== "Enter")
  );
}

// Arrow keys and Enter move between set cells. Left and right leave a cell
// only when its value is selected or the caret is at that edge.
export function handleSetCellKeyDown(event: KeyboardEvent<HTMLElement>) {
  const input = event.target;

  if (
    isTextEditingKey(event) ||
    !(input instanceof HTMLInputElement) ||
    !canLeaveInput(input, event.key)
  ) {
    return;
  }

  const cell = input.closest("td");

  if (!cell) {
    return;
  }

  const target = findTargetInput(cell, event);

  if (!target) {
    return;
  }

  event.preventDefault();
  target.focus();
}
